import { useTranslation } from 'react-i18next';

import {
  CountrySelect,
  DatePickerInput,
  Form,
  NumberInput,
  PhoneNumberInput,
  PLATFORM_THEME_COLORS,
  SelectInput,
  TextInput,
  Typography,
} from '@thinknet-gmbh/platform-common-ui';

import {
  DEFAULT_NAME_FIELD_REGEX,
  EmployeeStatusesEnum,
  GendersEnum,
  STREET_FIELD_REGEX,
  HOUSE_NUMBER_FIELD_REGEX,
  SalutationOptionsEnum,
} from '@thinknet-gmbh/platform-common';

import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetPermissionsGroups } from 'src/hooks/permission-group/get-permissions-groups.hook';

import { BooleanSelectInput } from '../../../../components/boolean-select-input';

import {
  ComponentWrapper,
  AccessWrapper,
  FieldsInfoWrapper,
} from '../questionnaire-form/questionnaire-form.styles';

const PersonalInfo = ({ parentField }: { parentField: string }) => {
  const { t, i18n } = useTranslation('employee');
  const currentCompanyId = useGetCurrentCompanyId();
  const { permissionsGroups } = useGetPermissionsGroups(currentCompanyId);

  return (
    <ComponentWrapper>
      <Typography weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
        {t('employeePersonalInfo.labels.personalInfo')}
      </Typography>

      <FieldsInfoWrapper>
        <Form.Item
          name={[parentField, 'salutation']}
          label={t('employee:employeePersonalInfo.labels.salutation')}
          rules={[
            {
              required: true,
              message: t('customers:error.fieldIsRequired'),
            },
          ]}
        >
          <SelectInput placeholder={t('employee:employeePersonalInfo.labels.salutationOptions.MR')}>
            {Object.keys(SalutationOptionsEnum).map(option => (
              <SelectInput.Option value={option} key={option}>
                {t(`employee:employeePersonalInfo.labels.salutationOptions.${option}`)}
              </SelectInput.Option>
            ))}
          </SelectInput>
        </Form.Item>

        <Form.Item
          name={[parentField, 'firstName']}
          label={t('common:labels.firstName')}
          rules={[
            { required: true, message: t('common:validation.error.firstNameRequired') },
            {
              pattern: DEFAULT_NAME_FIELD_REGEX,
              message: t('common:validation.error.incorrectFormat'),
            },
          ]}
        >
          <TextInput placeholder="Kate" maxLength={30} />
        </Form.Item>
        <Form.Item
          name={[parentField, 'lastName']}
          label={t('common:labels.lastName')}
          rules={[
            { required: true, message: t('common:validation.error.lastNameRequired') },
            {
              pattern: DEFAULT_NAME_FIELD_REGEX,
              message: t('common:validation.error.incorrectFormat'),
            },
          ]}
        >
          <TextInput placeholder="Olov" maxLength={30} />
        </Form.Item>
        <Form.Item
          name={[parentField, 'birthName']}
          label={t('employee:employeePersonalInfo.labels.birthName')}
        >
          <TextInput placeholder="Kate" maxLength={30} />
        </Form.Item>
        <Form.Item
          name={[parentField, 'gender']}
          label={t('employee:employeePersonalInfo.labels.gender')}
          rules={[
            {
              required: true,
              message: t('employee:employeePersonalInfo.validation.genderRequired'),
            },
          ]}
        >
          <SelectInput placeholder={t('employee:employeePersonalInfo.labels.female')}>
            {Object.keys(GendersEnum).map(gender => (
              <SelectInput.Option value={gender} key={gender}>
                {t(`employee:employeePersonalInfo.labels.${gender.toLowerCase()}`)}
              </SelectInput.Option>
            ))}
          </SelectInput>
        </Form.Item>

        <Form.Item
          name={[parentField, 'academicDegree']}
          label={t('employee:employeePersonalInfo.labels.academicDegree')}
        >
          <TextInput
            placeholder={t('employee:employeePersonalInfo.labels.professor')}
            maxLength={20}
          />
        </Form.Item>

        <Form.Item
          name={[parentField, 'status']}
          label={t('common:labels.status')}
          rules={[{ required: true, message: t('customers:error.fieldIsRequired') }]}
          initialValue={EmployeeStatusesEnum.INACTIVE}
        >
          <SelectInput placeholder="Active">
            {Object.keys(EmployeeStatusesEnum).map(status => {
              return (
                <SelectInput.Option value={status} key={status}>
                  {t(`employee:employeePersonalInfo.labels.statuses.${status.toLowerCase()}`)}
                </SelectInput.Option>
              );
            })}
          </SelectInput>
        </Form.Item>
        <Form.Item
          name={[parentField, 'employeeNumber']}
          label={t('employee:labels.employeeNumber')}
          rules={[
            { required: true, message: t('employee:validationError.employeeNumberRequired') },
            { type: 'number', message: t('common:validation.error.mustBeInteger') },
            {
              validator: (_, value) =>
                Number.isInteger(value)
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('common:validation.error.mustBeInteger'))),
            },
          ]}
        >
          <NumberInput
            maxLength={5}
            placeholder="44423"
            allowLeadingZeros={false}
            thousandSeparator={false}
            customInput={TextInput}
          />
        </Form.Item>
        <Form.Item
          name={[parentField, 'phoneNumber']}
          label={t('employee:labels.personalPhoneNumber')}
        >
          <PhoneNumberInput placeholder="+49 176 384 15749" />
        </Form.Item>

        <Form.Item
          name={[parentField, 'email']}
          label={t('common:labels.email')}
          rules={[
            { required: true, message: t('common:validation.error.emailRequired') },
            { type: 'email', message: t('common:validation.error.incorrectFormat') },
          ]}
        >
          <TextInput maxLength={50} placeholder="kateonolov@gmail.com" />
        </Form.Item>
        <Form.Item
          name={[parentField, 'personalEmail']}
          label={t('employee:labels.personalEmail')}
          rules={[
            { required: true, message: t('employee:validationError.personalEmailRequired') },
            { type: 'email', message: t('common:validation.error.incorrectFormat') },
          ]}
        >
          <TextInput maxLength={50} placeholder="kateonolov@gmail.com" />
        </Form.Item>
        <Form.Item
          name={[parentField, 'birthday']}
          label={t('employee:labels.birthday')}
          rules={[{ required: true, message: t('employee:validationError.birthdayRequired') }]}
        >
          <DatePickerInput lang={i18n.language} format="DD.MM.YYYY" />
        </Form.Item>
        <Form.Item
          name={[parentField, 'cityOfBirth']}
          label={t('employee:employeePersonalInfo.labels.cityOfBirth')}
        >
          <TextInput placeholder="Hamburg" maxLength={34} />
        </Form.Item>
        <Form.Item
          name={[parentField, 'countryOfBirth']}
          label={t('employee:employeePersonalInfo.labels.countryOfBirth')}
        >
          <CountrySelect t={t} placeholder={t('common:placeholders.country')} maxLength={50} />
        </Form.Item>
        <Form.Item
          name={[parentField, 'nationality']}
          label={t('employee:labels.nationality')}
          rules={[
            { required: true, message: t('employee:validationError.nationalityRequired') },
            {
              pattern: DEFAULT_NAME_FIELD_REGEX,
              message: t('common:validation.error.incorrectFormat'),
            },
          ]}
        >
          <CountrySelect t={t} placeholder={t('common:placeholders.country')} maxLength={50} />
        </Form.Item>
        <Form.Item name={[parentField, 'socials', 'linkedIn']} label="Linkedin">
          <TextInput maxLength={100} placeholder="http://linkedin/...." />
        </Form.Item>
        <Form.Item name={[parentField, 'socials', 'xing']} label="XING">
          <TextInput maxLength={100} placeholder="http://xing/...." />
        </Form.Item>

        <Form.Item
          name={[parentField, 'street']}
          label={t('common:labels.address.street')}
          rules={[
            { required: true, message: t('customers:error.fieldIsRequired') },
            {
              pattern: STREET_FIELD_REGEX,
              message: t('common:validation.error.streetFormat'),
            },
          ]}
        >
          <TextInput placeholder={t('common:placeholders.street')} maxLength={33} />
        </Form.Item>

        <Form.Item
          name={[parentField, 'houseNumber']}
          label={t('employee:employeePersonalInfo.labels.houseNumber')}
          rules={[
            { required: true, message: t('customers:error.fieldIsRequired') },
            {
              pattern: HOUSE_NUMBER_FIELD_REGEX,
              message: t('common:validation.error.houseNumberFormat'),
            },
          ]}
        >
          <TextInput placeholder="27321" maxLength={9} />
        </Form.Item>
        <Form.Item
          name={[parentField, 'country']}
          label={t('common:labels.address.country')}
          rules={[{ required: true, message: t('customers:error.fieldIsRequired') }]}
        >
          <CountrySelect t={t} placeholder={t('common:placeholders.country')} maxLength={50} />
        </Form.Item>
        <Form.Item
          name={[parentField, 'city']}
          label={t('common:labels.address.city')}
          rules={[{ required: true, message: t('customers:error.fieldIsRequired') }]}
        >
          <TextInput placeholder="Hamburg" maxLength={34} />
        </Form.Item>
        <Form.Item
          name={[parentField, 'zipCode']}
          label={t('common:labels.address.zipCode')}
          rules={[
            { required: true, message: t('customers:error.fieldIsRequired') },
            { type: 'number', message: t('common:validation.error.mustBeInteger') },
            {
              validator: (_, value) =>
                Number.isInteger(value)
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('common:validation.error.mustBeInteger'))),
            },
          ]}
        >
          <NumberInput
            thousandSeparator={false}
            allowLeadingZeros={false}
            placeholder="27321"
            maxLength={5}
          />
        </Form.Item>
      </FieldsInfoWrapper>
      <AccessWrapper>
        <Typography weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
          {t('employee:employeePersonalInfo.labels.access')}
        </Typography>
        <FieldsInfoWrapper>
          <Form.Item
            name={[parentField, 'accessToPlatform']}
            label={t('employee:employeePersonalInfo.labels.accessLabel')}
            rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
          >
            <BooleanSelectInput t={t} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const isAccess = getFieldValue([parentField, 'accessToPlatform']);

              return (
                <Form.Item
                  name={'permissionGroupId'}
                  label={t('employee:labels.permissionType')}
                  rules={[
                    {
                      required: isAccess,
                      message: t('employee:validationError.permissionTypeRequired'),
                    },
                  ]}
                >
                  <SelectInput disabled={!isAccess} placeholder="HR">
                    {permissionsGroups.map((permissionGroup: any) => (
                      <SelectInput.Option key={permissionGroup._id} value={permissionGroup._id}>
                        {permissionGroup.name}
                      </SelectInput.Option>
                    ))}
                  </SelectInput>
                </Form.Item>
              );
            }}
          </Form.Item>
        </FieldsInfoWrapper>
      </AccessWrapper>
    </ComponentWrapper>
  );
};

export default PersonalInfo;
